import React, { useEffect, useState } from 'react';
import { useCariStore, usePersonnelStore, useAuthStore } from '@/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  Phone,
  Mail,
  MapPin,
  Download,
  Upload,
  FileSpreadsheet,
  Loader2,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/components/I18nProvider';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomersPage() {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { caris, isLoading, fetchCaris, createCari, updateCari, deleteCari, bulkDeleteCari, bulkAssignCari } = useCariStore();
  const { personnel, fetchPersonnel } = usePersonnelStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCari, setEditingCari] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    phone: '',
    email: '',
    address: '',
    assigned_user_id: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const importFileRef = React.useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [bulkAssignUserId, setBulkAssignUserId] = useState('');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  useEffect(() => {
    fetchCaris();
    fetchPersonnel();
  }, [fetchCaris, fetchPersonnel]);

  const filteredCaris = caris.filter(
    (cari) =>
      cari.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cari.phone.includes(searchTerm) ||
      (cari.email && cari.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleOpenDialog = (cari = null) => {
    if (cari) {
      setEditingCari(cari);
      setFormData({
        title: cari.title,
        phone: cari.phone,
        email: cari.email || '',
        address: cari.address || '',
        assigned_user_id: cari.assigned_user_id || '',
      });
    } else {
      setEditingCari(null);
      setFormData({
        title: '',
        phone: '',
        email: '',
        address: '',
        assigned_user_id: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingCari(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const data = {
      title: formData.title,
      phone: formData.phone,
      email: formData.email?.trim() || null,
      address: formData.address?.trim() || null,
      assigned_user_id: formData.assigned_user_id === 'unassigned' ? null : (formData.assigned_user_id || null),
    };

    let result;
    if (editingCari) {
      result = await updateCari(editingCari.id, data);
    } else {
      result = await createCari(data);
    }

    setIsSubmitting(false);

    if (result.success) {
      toast.success(editingCari ? t('customerUpdated') : t('customerCreated'));
      handleCloseDialog();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu cariyi silmek istediğinizden emin misiniz?')) {
      const result = await deleteCari(id);
      if (result.success) {
        toast.success(t('customerDeleted'));
      } else {
        toast.error(result.error);
      }
    }
  };

  const handleToggleActive = async (cari) => {
    const result = await updateCari(cari.id, { is_active: !cari.is_active });
    if (result.success) {
      toast.success(`Cari ${!cari.is_active ? 'aktif' : 'pasif'} yapıldı`);
    } else {
      toast.error(result.error);
    }
  };

  const exportToExcel = async () => {
    try {
      const { getAuthAxios } = require('@/store');
      const api = getAuthAxios();
      const res = await api.get('/cari/export/excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'musteriler.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Dışa aktarma başarısız');
    }
  };

  const downloadTemplate = async () => {
    try {
      const { getAuthAxios } = require('@/store');
      const api = getAuthAxios();
      const res = await api.get('/cari/template/excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'musteri_sablonu.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Şablon indirme başarısız');
    }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Sadece Excel dosyası (.xlsx) yüklenebilir');
      return;
    }
    setIsImporting(true);
    try {
      const { getAuthAxios } = require('@/store');
      const api = getAuthAxios();
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/cari/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data;
      toast.success(`${data.imported} müşteri eklendi${data.skipped > 0 ? `, ${data.skipped} atlandı` : ''}`);
      if (data.errors?.length > 0) {
        data.errors.forEach((err) => toast.warning(err));
      }
      fetchCaris();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İçe aktarma başarısız');
    }
    setIsImporting(false);
    if (importFileRef.current) importFileRef.current.value = '';
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length} cariyi silmek istediğinizden emin misiniz? İlgili ödeme sözleri de silinecektir.`)) return;
    setIsBulkProcessing(true);
    const result = await bulkDeleteCari(selectedIds);
    setIsBulkProcessing(false);
    if (result.success) {
      toast.success(result.data.message);
      setSelectedIds([]);
    } else {
      toast.error(result.error);
    }
  };

  const handleBulkAssign = async () => {
    if (selectedIds.length === 0 || !bulkAssignUserId) return;
    setIsBulkProcessing(true);
    const result = await bulkAssignCari(selectedIds, bulkAssignUserId === 'unassigned' ? null : bulkAssignUserId);
    setIsBulkProcessing(false);
    if (result.success) {
      toast.success(result.data.message);
      setSelectedIds([]);
      setIsBulkAssignOpen(false);
      setBulkAssignUserId('');
      fetchCaris();
    } else {
      toast.error(result.error);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCaris.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredCaris.map(c => c.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';

  return (
    <div className="space-y-8 animate-fade-in" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Müşteriler</h1>
          <p className="mt-1 text-muted-foreground">Cari hesaplarınızı yönetin</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="template-btn">
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Şablon İndir
              </Button>
              <input type="file" ref={importFileRef} accept=".xlsx,.xls" className="hidden" onChange={handleExcelImport} data-testid="import-file-input" />
              <Button variant="outline" size="sm" onClick={() => importFileRef.current?.click()} disabled={isImporting} data-testid="import-btn">
                {isImporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Excel İçe Aktar
              </Button>
              <Button variant="outline" size="sm" onClick={exportToExcel} data-testid="export-btn">
                <Download className="mr-2 h-4 w-4" />
                Dışa Aktar
              </Button>
            </>
          )}
          <Button size="sm" onClick={() => handleOpenDialog()} data-testid="add-customer-btn">
            <Plus className="mr-2 h-4 w-4" />
            Cari Ekle
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Cari</p>
                <p className="font-heading text-xl font-bold">{caris.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-status-paid/10 p-2">
                <Users className="h-5 w-5 text-status-paid" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Aktif Cariler</p>
                <p className="font-heading text-xl font-bold">{caris.filter((c) => c.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-destructive/10 p-2">
                <Users className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pasif Cariler</p>
                <p className="font-heading text-xl font-bold">{caris.filter((c) => !c.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-status-pending/10 p-2">
                <Users className="h-5 w-5 text-status-pending" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Atanmış</p>
                <p className="font-heading text-xl font-bold">
                  {caris.filter((c) => c.assigned_user_id).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cari ara (unvan, telefon, e-posta)"
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-customers"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {isAdmin && selectedIds.length > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="p-3 flex items-center justify-between">
            <span className="text-sm font-medium">{selectedIds.length} cari seçildi</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsBulkAssignOpen(true)} disabled={isBulkProcessing} data-testid="bulk-assign-btn">
                <UserPlus className="mr-2 h-4 w-4" />
                Personel Ata
              </Button>
              <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={isBulkProcessing} data-testid="bulk-delete-btn">
                {isBulkProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                Toplu Sil
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])} data-testid="clear-selection-btn">
                Seçimi Temizle
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
        </div>
      ) : filteredCaris.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {searchTerm ? 'Arama sonucu bulunamadı' : t('noCustomers')}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="space-y-4 md:hidden">
            {filteredCaris.map((cari) => (
              <Card key={cari.id} data-testid={`customer-card-${cari.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate max-w-[180px]" title={cari.title}>{cari.title}</span>
                        <Badge variant={cari.is_active ? 'default' : 'secondary'} className="shrink-0 text-[10px]">
                          {cari.is_active ? 'Aktif' : 'Pasif'}
                        </Badge>
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                        <Phone className="h-3 w-3 shrink-0" />
                        <span>{cari.phone}</span>
                      </div>
                      {cari.email && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3 shrink-0" />
                          <span className="truncate">{cari.email}</span>
                        </div>
                      )}
                      {cari.address && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">{cari.address}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {isAdmin && (
                        <Switch
                          checked={cari.is_active}
                          onCheckedChange={() => handleToggleActive(cari)}
                          data-testid={`toggle-active-m-${cari.id}`}
                        />
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t pt-3">
                    <div className="text-xs text-muted-foreground">
                      {cari.assigned_user_name ? (
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{cari.assigned_user_name}</span>
                      ) : (
                        <span className="text-muted-foreground/60">Atanmamış</span>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenDialog(cari)} data-testid={`edit-customer-m-${cari.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(cari.id)} data-testid={`delete-customer-m-${cari.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <Card className="hidden md:block">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isAdmin && (
                      <TableHead className="w-12">
                        <Checkbox
                          checked={filteredCaris.length > 0 && selectedIds.length === filteredCaris.length}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                    )}
                    <TableHead>Unvan</TableHead>
                    <TableHead>{t('phone')}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t('email')}</TableHead>
                    <TableHead>Satış Personeli</TableHead>
                    <TableHead>{t('status')}</TableHead>
                    {isAdmin && <TableHead className="text-right">İşlemler</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCaris.map((cari) => (
                    <TableRow key={cari.id} data-testid={`customer-row-${cari.id}`} className={selectedIds.includes(cari.id) ? 'bg-primary/5' : ''}>
                      {isAdmin && (
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(cari.id)}
                            onCheckedChange={() => toggleSelect(cari.id)}
                            data-testid={`select-customer-${cari.id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="font-medium max-w-[200px] truncate" title={cari.title}>{cari.title}</div>
                        {cari.address && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate max-w-[180px]">{cari.address}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          {cari.phone}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {cari.email ? (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="truncate max-w-[180px]">{cari.email}</span>
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {cari.assigned_user_name || <span className="text-muted-foreground">Atanmamış</span>}
                      </TableCell>
                      <TableCell>
                        {isAdmin ? (
                          <Switch checked={cari.is_active} onCheckedChange={() => handleToggleActive(cari)} data-testid={`toggle-active-${cari.id}`} />
                        ) : (
                          <Badge variant={cari.is_active ? 'default' : 'secondary'}>{cari.is_active ? 'Aktif' : 'Pasif'}</Badge>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(cari)} data-testid={`edit-customer-${cari.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(cari.id)} data-testid={`delete-customer-${cari.id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingCari ? t('editCustomer') : t('addCustomer')}</DialogTitle>
            <DialogDescription>
              {editingCari ? 'Cari bilgilerini güncelleyin' : 'Yeni bir cari hesap oluşturun'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Unvan *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Firma veya kişi adı"
                required
                data-testid="cari-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefon (WhatsApp) *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="905xxxxxxxxx"
                required
                data-testid="cari-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="ornek@firma.com"
                data-testid="cari-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">{t('address')}</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Adres bilgisi"
                data-testid="cari-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="assigned_user">Satış Personeli</Label>
              <Select
                value={formData.assigned_user_id}
                onValueChange={(value) => setFormData({ ...formData, assigned_user_id: value })}
              >
                <SelectTrigger data-testid="cari-personnel-select">
                  <SelectValue placeholder="Personel seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Atanmamış</SelectItem>
                  {personnel.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                İptal
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="save-cari-btn">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : editingCari ? (
                  t('update')
                ) : (
                  'Oluştur'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={isBulkAssignOpen} onOpenChange={setIsBulkAssignOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Toplu Personel Atama</DialogTitle>
            <DialogDescription>
              {selectedIds.length} seçili cariye personel atayın
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Personel</Label>
              <Select value={bulkAssignUserId} onValueChange={setBulkAssignUserId}>
                <SelectTrigger data-testid="bulk-assign-select">
                  <SelectValue placeholder="Personel seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Atamayı Kaldır</SelectItem>
                  {personnel.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.short_id ? `[${p.short_id}] ` : ''}{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkAssignOpen(false)}>İptal</Button>
            <Button onClick={handleBulkAssign} disabled={!bulkAssignUserId || isBulkProcessing} data-testid="confirm-bulk-assign-btn">
              {isBulkProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
              Ata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
